import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';
import { Box, Typography, Chip, Tooltip, Link } from '@mui/material';
import { AppBulkActions, AppPagination, EmployeeDisplay } from '@/__core/components';
import ConfirmationModal from '@/__core/components/ConfirmationModal';
import { useTheme } from '@mui/material/styles';
import {
  formatVietnameseDateTime,
  formatVietnameseFullDateTime
} from '@/__core/helpers/utils';
import DataTableModule from '@/__core/layout/module/DataTableModule';
import { Transaction } from '../models/Transaction';
import { ColumnConfig } from './ColumnVisibilityDropdown';
import OrderDetailModal from './OrderDetailModal';
import { sharedListStyles } from '@/__core/styles/sharedListStyles';
import { getStatusMetaByModule } from '../utils/statusByModuleCache';

// Format date as dd-mm-yyyy
const formatDateDDMMYYYY = (dateString: string) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (error) {
    return '-';
  }
};

export type MainListTransactionHandle = {
  requestDelete: () => void;
  requestApprove: () => void;
};

interface MainListTransactionProps {
  transactions: Transaction[];
  activeCompanyFilter?: string;
  pagination?: {
    count: number;
    page: number;
    pageSize: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
  onPageChange?: (event: React.ChangeEvent<unknown>, page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onRowClick?: (transaction: Transaction) => void;
  onSelectionChange?: (selectedIds: string[]) => void;
  onApproveTransactions?: (transactionIds: string[]) => void | Promise<void>;
  onSetPendingTransactions?: (transactionIds: string[]) => void | Promise<void>;
  onDeleteTransactions?: (transactionIds: string[]) => void | Promise<void>;
  canDeleteTransactions?: boolean;
  canUpdateTransactions?: boolean;
  canApproveTransactions?: boolean;
  canReadOrders?: boolean;
  columns?: ColumnConfig[];
}

type StatusChipPalette = {
  bg: string;
  color: string;
  border: string;
};

const DEFAULT_STATUS_CHIP_PALETTE: StatusChipPalette = {
  bg: '#f3f4f6',
  color: '#6b7280',
  border: '#d1d5db',
};

type StatusMeta = {
  name: string;
  color?: string;
};

const MainListTransaction = forwardRef<MainListTransactionHandle, MainListTransactionProps>(
  function MainListTransaction(
    {
      transactions,
      activeCompanyFilter = 'all',
      pagination,
      onPageChange,
      onPageSizeChange,
      onRowClick,
      onSelectionChange,
      onApproveTransactions,
      onSetPendingTransactions,
      onDeleteTransactions,
      canDeleteTransactions = false,
      canUpdateTransactions = false,
      canApproveTransactions = false,
      canReadOrders = false,
      columns = [],
    },
    ref
  ) {
  // Modal state
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [selectedOrderCode, setSelectedOrderCode] = useState<string>('');

  const theme = useTheme();
  const [statusMetaById, setStatusMetaById] = useState<Record<string, StatusMeta>>({});

  // Modal handlers
  const handleOrderClick = (event: React.MouseEvent, orderId: string, orderCode?: string) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canReadOrders) {
      return;
    }
    setSelectedOrderId(orderId);
    setSelectedOrderCode(orderCode || '');
    setOrderModalOpen(true);
  };

  const handleOrderModalClose = () => {
    setOrderModalOpen(false);
    setSelectedOrderId(null);
    setSelectedOrderCode('');
  };
  
  const displayTransactions = useMemo(() => {
    if (!transactions?.length) return [];
    return transactions;
  }, [transactions, activeCompanyFilter]);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [applyingAction, setApplyingAction] = useState(false);

  useEffect(() => {
    const currentIds = new Set(displayTransactions.map((t) => t.id));
    setSelectedIds((prev) => prev.filter((id) => currentIds.has(id)));
  }, [displayTransactions]);

  useEffect(() => {
    onSelectionChange?.(selectedIds);
  }, [onSelectionChange, selectedIds]);

  const filteredTransactions = displayTransactions;
  const getTypeColor = (type: string) => {
    if (!type) {
      return { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' };
    }
    switch (type.toLowerCase()) {
      case 'income':
        return { bg: '#dcfce7', color: '#166534', border: '#bbf7d0' };
      case 'expense':
        return { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' };
      default:
        return { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' };
    }
  };

  const hexToRgba = (hex: string, alpha: number) => {
    const normalized = hex.replace('#', '').trim();
    if (!/^[\da-fA-F]{3}$|^[\da-fA-F]{6}$/.test(normalized)) {
      return undefined;
    }

    const fullHex = normalized.length === 3
      ? normalized.split('').map((char) => char + char).join('')
      : normalized;

    const r = parseInt(fullHex.slice(0, 2), 16);
    const g = parseInt(fullHex.slice(2, 4), 16);
    const b = parseInt(fullHex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const pickStatusColorFromTransaction = (row: any): string | undefined => {
    const candidates = [
      row?.status?.color,
      row?.status_color,
      row?.transaction_status?.color,
      row?.finance_status?.color,
      row?.approval_status?.color,
      row?.workflow_status?.color,
    ];

    const color = candidates.find((value) => typeof value === 'string' && value.trim().length > 0);
    return typeof color === 'string' ? color : undefined;
  };

  const pickTransactionTypeColor = (row: any): string | undefined => {
    const candidates = [
      row?.transaction_type?.color,
      row?.transaction_type?.category_color,
      row?.transaction_type_color,
      row?.transaction_type?.category_color,
    ];

    const color = candidates.find((value) => typeof value === 'string' && value.trim().length > 0);
    return typeof color === 'string' ? color : undefined;
  };

  const getStatusKey = (row: any): string => {
    if (typeof row?.status === 'string') return row.status;
    return row?.status?.id || row?.status_code || row?.status_value || '';
  };

  const isLikelyId = (value: string): boolean => {
    const trimmed = (value || '').trim();
    if (!trimmed) return false;
    const uuidLike = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidLike.test(trimmed);
  };

  const getStatusLabel = (row: any): string => {
    const statusName = row?.status?.name || row?.status_name || row?.status_label;
    if (typeof statusName === 'string' && statusName.trim().length > 0) {
      return statusName;
    }

    const statusKey = getStatusKey(row);
    const mappedName = statusMetaById[statusKey]?.name;
    if (mappedName) return mappedName;
    if (statusKey === 'pending') return 'Chờ duyệt';
    if (statusKey === 'approved') return 'Duyệt';
    if (isLikelyId(statusKey)) return '-';
    return statusKey || '-';
  };

  const getStatusChipPalette = (row: any): StatusChipPalette => {
    const statusKey = getStatusKey(row);
    const mappedColor = statusMetaById[statusKey]?.color;
    const apiColor = pickStatusColorFromTransaction(row) || mappedColor;
    if (apiColor && typeof apiColor === 'string' && apiColor.trim().length > 0) {
      return {
        bg: hexToRgba(apiColor, 0.14) || '#f3f4f6',
        color: apiColor,
        border: apiColor,
      };
    }

    return DEFAULT_STATUS_CHIP_PALETTE;
  };

  const statusMetaRequestedRef = React.useRef(false);

  useEffect(() => {
    const needsMeta = filteredTransactions.some((row: any) => {
      const key = getStatusKey(row);
      return isLikelyId(key) && !statusMetaById[key];
    });

    if (!needsMeta || statusMetaRequestedRef.current) {
      return;
    }

    statusMetaRequestedRef.current = true;
    let cancelled = false;

    getStatusMetaByModule()
      .then((map) => {
        if (!cancelled) {
          setStatusMetaById(map);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStatusMetaById({});
        }
      });

    return () => {
      cancelled = true;
    };
  }, [filteredTransactions, statusMetaById]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN').format(amount);
  };

  // Define all possible columns
  const allColumns = [
    {
      id: 'posting_date',
      label: 'Ngày thu chi',
      render: (value: any, row: Transaction) => (
        <Typography sx={sharedListStyles.cellText}>
          {formatDateDDMMYYYY(row.posting_date || '')}
        </Typography>
      ),
    },
    {
      id: 'document_date',
      label: 'Ngày chứng từ',
      render: (value: any, row: Transaction) => (
        <Typography sx={sharedListStyles.cellText}>
          {formatDateDDMMYYYY(row.document_date || '')}
        </Typography>
      ),
    },
    {
      id: 'transaction_type',
      label: 'Loại',
      render: (value: any, row: Transaction) => {
        const apiColor = pickTransactionTypeColor(row);
        const fallback = getTypeColor(row.transaction_type_category || '').color;
        const colorToUse = apiColor || fallback;
        return (
          <Typography
            sx={{
              ...sharedListStyles.cellText,
              color: colorToUse,
              fontWeight: 500,
            }}
          >
            {row.transaction_type_name || '-'}
          </Typography>
        );
      },
    },
    {
      id: 'transaction_type_category',
      label: 'Loại thu/chi',
      render: (value: any, row: Transaction) => (
        <Chip
          label={row.transaction_type_category === 'income' ? 'Thu nhập' : row.transaction_type_category === 'expense' ? 'Chi phí' : '-'}
          size="small"
          sx={{
            ...sharedListStyles.cellChipBase,
            backgroundColor: getTypeColor(row.transaction_type_category || '').bg,
            color: getTypeColor(row.transaction_type_category || '').color,
            border: `1px solid ${getTypeColor(row.transaction_type_category || '').border}`,
          }}
        />
      ),
    },
    {
      id: 'actual_amount',
      label: 'Thực thu/chi',
      align: 'right' as const,
      render: (value: any, row: Transaction) => (
        <Typography sx={{
          ...sharedListStyles.cellText,
          textAlign: 'right'
        }}>
          {formatCurrency(row.actual_amount || 0)}
        </Typography>
      ),
    },
    {
      id: 'total_amount',
      label: 'Tổng giá trị',
      align: 'right' as const,
      render: (value: any, row: Transaction) => (
        <Typography sx={{
          ...sharedListStyles.cellText,
          textAlign: 'right'
        }}>
          {formatCurrency(row.total_amount || 0)}
        </Typography>
      ),
    },
    {
      id: 'wallet',
      label: 'Tài khoản/Ví',
      render: (value: any, row: Transaction) => (
        <Typography sx={sharedListStyles.cellText}>
          {row.wallet_name || row.wallet || '-'}
        </Typography>
      ),
    },
    {
      id: 'note',
      label: 'Ghi chú',
      render: (value: string) => (
        <Typography sx={sharedListStyles.cellText}>
          {value || ''}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: 'Trạng thái',
      render: (value: any, row: Transaction) => {
        const statusPalette = getStatusChipPalette(row);
        return (
          <Typography
            sx={{
              ...sharedListStyles.cellText,
              color: statusPalette.color,
              fontWeight: 500,
            }}
          >
            {getStatusLabel(row)}
          </Typography>
        );
      },
    },
    
          {
            id: 'order',
            label: 'Đơn hàng',
            render: (value: any, row: Transaction) => {
              const orderFromRow = row.order && typeof row.order === 'object' ? (row.order as any) : null;
              const orderId =
                orderFromRow?.id ||
                orderFromRow?.uuid ||
                (row as any).order_id ||
                (typeof row.order === 'string' ? row.order : '');

              let orderCode =
                orderFromRow?.code ||
                orderFromRow?.order_code ||
                (row as any).order_code ||
                (row as any).order_name ||
                orderFromRow?.name ||
                '';

              if (!orderCode && typeof row.order === 'string' && row.order.length > 0) {
                orderCode = row.order.substring(0, 8) + '...';
              }

              const customerName =
                row.customer_name ||
                orderFromRow?.customer_name ||
                (row as any).order_customer_name ||
                '';

              if (!orderCode && !customerName) {
                return (
                  <Typography sx={sharedListStyles.cellText}>
                    -
                  </Typography>
                );
              }

              const displayText = orderCode && customerName
                ? `${orderCode} : ${customerName}`
                : orderCode || customerName;

              if (orderId) {
                return canReadOrders ? (
                  <Link
                    href="#"
                    onClick={(event) => handleOrderClick(event, orderId, orderCode || customerName || '-')}
                    sx={{
                      ...sharedListStyles.cellText,
                      color: theme.palette.primary.main,
                      textDecoration: 'none',
                      cursor: 'pointer',
                      transition: 'color 0.2s ease-in-out, text-decoration 0.2s ease-in-out',
                      padding: '2px 4px',
                      margin: '-2px -4px',
                      borderRadius: '4px',
                      '&:hover': {
                        color: theme.palette.primary.dark,
                        textDecoration: 'underline',
                        backgroundColor: theme.palette.action.hover,
                      }
                    }}
                  >
                    {displayText}
                  </Link>
                ) : (
                  <Typography sx={sharedListStyles.cellText}>
                    {displayText}
                  </Typography>
                );
              }

              return (
                <Typography sx={sharedListStyles.cellText}>
                  {displayText}
                </Typography>
              );
            },
          },
    {
      id: 'supplier',
      label: 'Nhà cung cấp',
      render: (value: any, row: Transaction) => {
        // Check if customer_type is supplier to determine if this is a supplier
        if (row.customer_type === 'supplier' && row.customer_name) {
          return (
            <Typography sx={sharedListStyles.cellText}>
              {row.customer_name}
            </Typography>
          );
        }

        // If not a supplier or no name, show -
        return (
          <Typography sx={sharedListStyles.cellText}>
            -
          </Typography>
        );
      },
    },
    {
      id: 'employee',
      label: 'Nhân viên',
      width: '180px',
      render: (value: any, row: Transaction) => {
        const emp = (row as any).employee;
        if (emp && typeof emp === 'object') {
          return (
            <EmployeeDisplay
              name={emp.name || ''}
              nickname={emp.nickname || ''}
              avatar={emp.user?.avatar || null}
              image1={emp.image1 || null}
              employeeId={emp.id}
              userId={emp.user?.id}
            />
          );
        }
        return (
          <Typography sx={sharedListStyles.cellText}>
            {row.employee_name || '-'}
          </Typography>
        );
      },
    },
    {
      id: 'created_at',
      label: 'Ngày tạo',
      render: (value: any, row: Transaction) => (
        <Tooltip title={formatVietnameseFullDateTime(row.created_at || '')} placement="top">
          <Typography sx={sharedListStyles.cellText}>
            {formatVietnameseDateTime(row.created_at || '')}
          </Typography>
        </Tooltip>
      ),
    },
    {
      id: 'created_by',
      label: 'Người tạo',
      render: (value: any, row: Transaction) => {
        // Check if we have created_by_name (from backend serializer)
        if (row.created_by_name && row.created_by_name !== row.created_by) {
          return (
            <Typography sx={sharedListStyles.cellText}>
              {row.created_by_name}
            </Typography>
          );
        }

        // If created_by_name is not available or same as created_by ID, show -
        return (
          <Typography sx={sharedListStyles.cellText}>
            -
          </Typography>
        );
      },
    },
  ];

  // Filter columns based on visibility configuration
  const hiddenByDefault = new Set(['transaction_type_category']);
  const visibleColumns = columns.length > 0 
    ? allColumns.filter(col => {
        const columnConfig = columns.find(c => c.id === col.id);
        if (columnConfig) {
          return columnConfig.visible !== false;
        }
        return !hiddenByDefault.has(col.id);
      })
    : allColumns.filter(col => !hiddenByDefault.has(col.id));

  const totalCount = pagination?.count || 0;
  const totalPages = pagination?.totalPages || 1;
  const currentPage = pagination?.page || 1;
  const pageSize = pagination?.pageSize || filteredTransactions.length || DEFAULT_PAGE_SIZE;
  const selectedCount = selectedIds.length;
  const canBulkSelect = canDeleteTransactions || canUpdateTransactions || canApproveTransactions;

  const areAllRowsSelected = useMemo(
    () => filteredTransactions.length > 0 && selectedIds.length === filteredTransactions.length,
    [filteredTransactions.length, selectedIds.length]
  );
  const areSomeRowsSelected = useMemo(
    () => selectedIds.length > 0 && selectedIds.length < filteredTransactions.length,
    [filteredTransactions.length, selectedIds.length]
  );

  const handleTransactionSelect = (transactionId: string) => {
    if (!canBulkSelect) return;
    setSelectedIds((prev) =>
      prev.includes(transactionId) ? prev.filter((id) => id !== transactionId) : [...prev, transactionId]
    );
  };

  const handleSelectAll = () => {
    if (!canBulkSelect) return;
    if (areAllRowsSelected) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(filteredTransactions.map((t) => t.id));
  };

  const runDelete = useCallback(async () => {
    if (!canDeleteTransactions || selectedIds.length === 0 || !onDeleteTransactions) {
      return;
    }
    await Promise.resolve(onDeleteTransactions([...selectedIds]));
    setSelectedIds([]);
  }, [canDeleteTransactions, onDeleteTransactions, selectedIds]);

  const openDeleteConfirm = useCallback(() => {
    if (!canDeleteTransactions) return;
    if (selectedIds.length === 0) return;
    setConfirmDeleteOpen(true);
  }, [canDeleteTransactions, selectedIds.length]);

  const requestApprove = useCallback(() => {
    if (!canApproveTransactions || selectedIds.length === 0) return;
    void onApproveTransactions?.([...selectedIds]);
    setSelectedIds([]);
  }, [canApproveTransactions, onApproveTransactions, selectedIds]);

  useImperativeHandle(
    ref,
    () => ({
      requestDelete: openDeleteConfirm,
      requestApprove,
    }),
    [openDeleteConfirm, requestApprove]
  );

  useEffect(() => {
    if (selectedCount === 0 && bulkAction) {
      setBulkAction('');
    }
  }, [bulkAction, selectedCount]);

  const handleApplyBulkAction = async (actionToApply?: string) => {
    const effectiveAction = actionToApply || bulkAction;
    if (!effectiveAction || selectedCount === 0) {
      return;
    }

    try {
      setApplyingAction(true);

      if (effectiveAction === 'delete') {
        openDeleteConfirm();
        setBulkAction('');
        return;
      }

      if (effectiveAction === 'pending') {
        if (onSetPendingTransactions) {
          await Promise.resolve(onSetPendingTransactions([...selectedIds]));
        }
        setSelectedIds([]);
        setBulkAction('');
        return;
      }

      if (effectiveAction === 'approve') {
        if (onApproveTransactions) {
          await Promise.resolve(onApproveTransactions([...selectedIds]));
        }
        setSelectedIds([]);
        setBulkAction('');
      }
    } finally {
      setApplyingAction(false);
    }
  };

  return (
    <Box sx={sharedListStyles.container}>
      <DataTableModule
        data={filteredTransactions}
        columns={visibleColumns}
        leftFilterWidth={0}
        onRowSelect={canBulkSelect ? handleTransactionSelect : () => {}}
        onSelectAll={canBulkSelect ? handleSelectAll : () => {}}
        onRowClick={onRowClick}
        getRowId={(row) => row.id}
        isRowSelected={(row) => selectedIds.includes(row.id)}
        areAllRowsSelected={areAllRowsSelected}
        areSomeRowsSelected={areSomeRowsSelected}
        showCheckbox={canBulkSelect}
      />
      {selectedCount > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            px: 0,
            py: 2,
            boxSizing: 'border-box',
            gap: 2,
          }}
        >
          <AppBulkActions
            selectedCount={selectedCount}
            itemLabel="thu chi"
            action={bulkAction}
            loading={applyingAction}
            actions={[
              { value: 'delete', label: 'Xóa', disabled: !onDeleteTransactions || !canDeleteTransactions },
              { value: 'pending', label: 'Chờ duyệt', disabled: !onSetPendingTransactions || !canUpdateTransactions },
              { value: 'approve', label: 'Duyệt', disabled: !onApproveTransactions || !canApproveTransactions },
            ]}
            onActionChange={setBulkAction}
            onApply={(selectedAction) => { void handleApplyBulkAction(selectedAction); }}
          />
          {totalCount > 0 && (
            <AppPagination
              count={totalCount}
              page={currentPage}
              pageSize={pageSize}
              totalPages={totalPages}
              itemLabel="thu chi"
              inline
              containerSx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
            />
          )}
        </Box>
      )}
      {selectedCount === 0 && (
        <AppPagination
          count={totalCount}
          page={currentPage}
          pageSize={pageSize}
          totalPages={totalPages}
          itemLabel="thu chi"
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}

      <ConfirmationModal
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={() => {
          setConfirmDeleteOpen(false);
          void runDelete();
        }}
        title='Xóa thu chi'
        message={`Bạn có chắc chắn muốn xóa ${selectedIds.length} thu chi đã chọn?`}
        confirmText='Xóa'
        cancelText='Hủy'
      />

      <OrderDetailModal
        open={orderModalOpen}
        onClose={handleOrderModalClose}
        orderId={selectedOrderId}
        orderCode={selectedOrderCode}
      />
    </Box>
  );
});

export default MainListTransaction;